class DotoProductVitrine {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-vitrine-viewport]');
    this.track = root.querySelector('[data-vitrine-track]');
    this.cards = Array.from(root.querySelectorAll('[data-vitrine-card]'));
    this.prev = root.querySelector('[data-vitrine-prev]');
    this.next = root.querySelector('[data-vitrine-next]');
    this.autoplay = root.dataset.autoplay === 'true';
    this.loop = root.dataset.loop === 'true';
    this.pauseHover = root.dataset.pauseHover === 'true';
    this.delay = (Number.parseInt(root.dataset.autoplaySpeed || '5', 10) || 5) * 1000;
    this.timer = null;
    this.resumeTimer = null;
    this.scrollFrame = null;

    if (!this.viewport || !this.track || this.cards.length === 0) return;

    this.onPrev = () => this.move(-1, true);
    this.onNext = () => this.move(1, true);
    this.onScroll = () => this.requestUpdate();
    this.onPointerDown = () => this.pauseForInteraction();
    this.onKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1, true);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1, true);
      }
    };
    this.onVisibility = () => {
      if (document.hidden) this.stop();
      else this.start();
    };
    this.onMouseEnter = () => {
      if (this.pauseHover) this.stop();
    };
    this.onMouseLeave = () => {
      if (this.pauseHover) this.start();
    };

    this.bind();
    this.updateControls();
    this.start();
  }

  bind() {
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.viewport.addEventListener('touchstart', this.onPointerDown, { passive: true });
    this.viewport.addEventListener('keydown', this.onKeydown);
    this.root.addEventListener('mouseenter', this.onMouseEnter);
    this.root.addEventListener('mouseleave', this.onMouseLeave);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  getStep() {
    const first = this.cards[0];
    const second = this.cards[1];
    if (!first) return this.viewport.clientWidth;
    if (!second) return first.getBoundingClientRect().width;
    return second.offsetLeft - first.offsetLeft;
  }

  getMaxScroll() {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  atStart() {
    return this.viewport.scrollLeft <= 2;
  }

  atEnd() {
    return this.viewport.scrollLeft >= this.getMaxScroll() - 2;
  }

  move(direction, userInitiated = false) {
    const step = this.getStep();
    const max = this.getMaxScroll();
    let target = this.viewport.scrollLeft + (direction * step);

    if (direction > 0 && target > max - 2) {
      target = this.loop ? 0 : max;
    }

    if (direction < 0 && target < 2) {
      target = this.loop ? max : 0;
    }

    this.viewport.scrollTo({ left: target, behavior: 'smooth' });
    if (userInitiated) this.pauseForInteraction();
  }

  pauseForInteraction() {
    this.stop();
    window.clearTimeout(this.resumeTimer);
    this.resumeTimer = window.setTimeout(() => this.start(), Math.max(this.delay, 5000));
  }

  requestUpdate() {
    if (this.scrollFrame) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateControls();
    });
  }

  updateControls() {
    if (!this.loop) {
      if (this.prev) this.prev.disabled = this.atStart();
      if (this.next) this.next.disabled = this.atEnd();
    } else {
      if (this.prev) this.prev.disabled = false;
      if (this.next) this.next.disabled = false;
    }
  }

  start() {
    this.stop();
    if (!this.autoplay || this.cards.length <= 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = window.setInterval(() => this.move(1, false), this.delay);
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  revealBlock(blockId) {
    const card = this.cards.find((item) => item.dataset.blockId === blockId);
    if (!card) return;
    this.stop();
    this.viewport.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
  }

  destroy() {
    this.stop();
    window.clearTimeout(this.resumeTimer);
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('scroll', this.onScroll);
    this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport?.removeEventListener('touchstart', this.onPointerDown);
    this.viewport?.removeEventListener('keydown', this.onKeydown);
    this.root.removeEventListener('mouseenter', this.onMouseEnter);
    this.root.removeEventListener('mouseleave', this.onMouseLeave);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }
}

const dotoProductVitrineInstances = new Map();

function initDotoProductVitrine(root) {
  if (!root || dotoProductVitrineInstances.has(root)) return;
  dotoProductVitrineInstances.set(root, new DotoProductVitrine(root));
}

function destroyDotoProductVitrine(root) {
  const instance = dotoProductVitrineInstances.get(root);
  instance?.destroy();
  dotoProductVitrineInstances.delete(root);
}

function initDotoProductVitrines(scope = document) {
  if (scope.matches?.('[data-doto-product-vitrine]')) initDotoProductVitrine(scope);
  scope.querySelectorAll?.('[data-doto-product-vitrine]').forEach(initDotoProductVitrine);
}

initDotoProductVitrines();

document.addEventListener('shopify:section:load', (event) => {
  initDotoProductVitrines(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.matches?.('[data-doto-product-vitrine]')
    ? event.target
    : event.target.querySelector?.('[data-doto-product-vitrine]');
  if (root) destroyDotoProductVitrine(root);
});

document.addEventListener('shopify:block:select', (event) => {
  const card = event.target.closest?.('[data-vitrine-card]');
  const root = card?.closest('[data-doto-product-vitrine]');
  if (!card || !root) return;
  initDotoProductVitrine(root);
  dotoProductVitrineInstances.get(root)?.revealBlock(card.dataset.blockId);
});

document.addEventListener('shopify:block:deselect', (event) => {
  const root = event.target.closest?.('[data-doto-product-vitrine]');
  if (!root) return;
  dotoProductVitrineInstances.get(root)?.start();
});
